import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  FormHelperText,
  Checkbox,
  ListItemText,
  CircularProgress,
  Chip,
  Autocomplete,
  TextField,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import {
  Link,
  useNavigate,
  useParams,
  useOutletContext,
} from "react-router-dom";
import MpgInput from "../../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../../CommonFormComp/MpgItem";
import MpgFileUpload from "../../../../../fileHandling/MpgFileUpload";
import { saveAs } from "file-saver";
import MuiLink from "@mui/material/Link";
import { editProgrammeDescriptorValidation } from "./editProgrammeDescriptorValidation";
import { managePDViewPath } from "../../../../../routes/routePaths";
import MpgErrorDialog from "../../../../../common/dialog/MpgErrorDialog";
import MpgLoadingDialog from "../../../../../common/dialog/MpgLoadingDialog";
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useUpdateProgramDescriptorMutation,
  useUploadProgramDescriptorFileMutation,
  useViewProgrammeDescriptorListMutation,
  useGetProgramDescriptorOwnersQuery,
} from "../../../../../reactQueries/AppApi/rqHooksForProgrammeDescriptor";
import { useFileDownloadMutation } from "../../../../../reactQueries/commonRQHooks";
import useTrackUserActivity from "../../../../../userActivityTracking/useTrackUserActivity";
import { getGUIDAndExtentionFromFileURL } from "../../../../../hooksAndFunctions/functions/getGUIDAndExtentionFromFileURL";
import { getAccessTypes } from "../../../../../hooksAndFunctions/functions/getAccessTypes";

const SelectSpinner = () => (
  <Box
    sx={{
      position: "absolute",
      right: 32,
      top: "50%",
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      pointerEvents: "none",
      zIndex: 1,
    }}
  >
    <CircularProgress size={16} color="inherit" />
  </Box>
);

const parseResourceUpdatedToInputValue = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  const ddmmyyyy = raw.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (ddmmyyyy) {
    const [, dd, mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}-${dd}`;
  }
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  return "";
};

const fixedSelectSx = {
  textAlign: "left",
  height: "40px",
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "0px 8px",
    overflow: "hidden",
  },
};

const baseMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
};

const bottomMenuProps = (ref) => ({
  ...baseMenuProps,
  PaperProps: {
    style: { width: ref.current?.offsetWidth ?? "auto" },
    sx: {
      maxHeight: "200px",
      overflowY: "auto",
      boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
      borderRadius: "4px",
      mt: 0.5,
    },
  },
  MenuListProps: { sx: { padding: 0 } },
  modifiers: [
    { name: "flip", enabled: false },
    { name: "preventOverflow", enabled: false },
  ],
});

const EditProgrammeDescriptor = ({ programDescriptorById }) => {
  const { id: programDescriptorId } = useParams();
  const navigate = useNavigate();
  const resourcesCMSData = useOutletContext();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    emailAddress,
    clientPortalAdminRoleId,
    isEnterpriseAdmin,
    id: userId,
  } = loggedInUser;

  const { clientPortalAdminType } = getAccessTypes();
  const { hasA4Access, hasA1Access } = clientPortalAdminType;
  const canViewNewFields = isEnterpriseAdmin || hasA1Access;

  const languageRef = useRef(null);
  const ownerRef = useRef(null);
  const durationRef = useRef(null);

  const { schema } = editProgrammeDescriptorValidation();

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      pdTitle: "",
      countries: [],
      owner: "",
      language: "",
      resourceUpdated: "",
      duration: [],
      pdFile: [],
    },
    mode: "onChange",
  });

  const {
    watch,
    reset,
    control,
    setValue,
    formState: { errors },
  } = rhfFormInstance;

  const inputPDTitle = watch("pdTitle");
  const selectedPdFile = watch("pdFile");
  const isFileModified = !!selectedPdFile?.[0];

  const { data: masterData, isLoading: isMasterDataLoading } =
    useGetProgramDescriptorOwnersQuery();

  const owners = masterData?.Owners || [];
  const countryOptions = masterData?.Countries || [];

  const countryNames = useMemo(
    () => countryOptions.map((c) => c.CountryName),
    [countryOptions]
  );

  const languageOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (masterData?.Languages || []).map((lang) => [lang.LocaleName, lang])
        ).values()
      ),
    [masterData?.Languages]
  );

  const durationOptions = masterData?.Durations || [];

  useEffect(() => {
    if (programDescriptorById && owners.length > 0) {
      reset({
        pdTitle: programDescriptorById?.ProgramDescripitorTitle || "",
        countries: Array.isArray(programDescriptorById?.Countries)
          ? programDescriptorById.Countries.map((x) =>
              typeof x === "string" ? x : x?.Country || ""
            ).filter(Boolean)
          : [],
        owner: programDescriptorById?.ResourceOwner || "",
        language: programDescriptorById?.Language || "",
        resourceUpdated: parseResourceUpdatedToInputValue(
          programDescriptorById?.ResourceUpdated
        ),
        duration: Array.isArray(programDescriptorById?.Durations)
          ? programDescriptorById.Durations.map((x) =>
              typeof x === "string" ? x : x?.Duration || ""
            ).filter(Boolean)
          : [],
        pdFile: [],
      });
    }
  }, [programDescriptorById, reset, owners]);

  useEffect(() => {
    if (isFileModified) {
      setValue("resourceUpdated", new Date().toISOString().split("T")[0], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isFileModified, setValue]);

  const { mutate: getPDList, data: PDData } =
    useViewProgrammeDescriptorListMutation();

  useEffect(() => {
    if (loggedInUser?.id) {
      getPDList({
        loggedInUserId: userId,
        roleId: clientPortalAdminRoleId,
        isEnterpriseAdmin,
      });
    }
  }, [loggedInUser, userId, clientPortalAdminRoleId, isEnterpriseAdmin]);

  const filteredPDListForStandardType =
    PDData?.data?.filter(
      (eachItem) => eachItem?.ProgramDescripitorType === "Standard"
    ) || [];

  const {
    mutate: DownloadPDfile,
    isError: isErrorOnDownload,
    isSuccess: isSuccessOnDownload,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  const {
    mutate: updateProgramDescriptor,
    isError: isErrorOnUpdate,
    isLoading: isLoadingOnUpdate,
    isSuccess: isSuccessOnUpdate,
  } = useUpdateProgramDescriptorMutation();

  const {
    mutate: uploadPDFile,
    isError: isErrorOnPDFileUpload,
    isLoading: isLoadingOnPDFileUpload,
    isSuccess: isSuccessOnPDFileUpload,
    data: dataOnPDFileUpload,
    error: errorOnPDFileUpload,
  } = useUploadProgramDescriptorFileMutation();

  const [fileDetails, setFileDetails] = useState({});

  useEffect(() => {
    if (programDescriptorById?.ProgramDescripitorFileName) {
      setFileDetails({
        ProgramDescripitorFileName:
          programDescriptorById?.ProgramDescripitorFileName || "",
        ProgramDescripitorFileGuid:
          programDescriptorById?.ProgramDescripitorFileGuid || "",
        ProgramDescripitorFileSize:
          programDescriptorById?.ProgramDescripitorFileSize || "",
      });
    } else {
      setFileDetails({});
    }
  }, [programDescriptorById?.ProgramDescripitorFileName]);

  const [bodyDataForUpdate, setBodyDataForUpdate] = useState({});

  const onSubmit = (data) => {
    const body = {
      Id: programDescriptorById?.Id,
      ProgramDescripitorTitle: data?.pdTitle || "",
      ProgramDescripitorType:
        programDescriptorById?.ProgramDescripitorType || "",
      IsActive: programDescriptorById?.IsActive,
      UpdatedBy: emailAddress || "",
      ProgramDescripitorURL: programDescriptorById?.ProgramDescripitorURL || "",
      ProgramDescripitorFileName:
        programDescriptorById?.ProgramDescripitorFileName || "",
      ProgramDescripitorFileGuid:
        programDescriptorById?.ProgramDescripitorFileGuid || "",
      ProgramDescripitorFileSize:
        programDescriptorById?.ProgramDescripitorFileSize || "",
      Language: data?.language || "",
      ResourceOwner: data?.owner || "",
      ResourceUpdated: data?.resourceUpdated || "",
      Durations: data?.duration || [],
      Countries: data?.countries || [],
    };

    setBodyDataForUpdate(body);

    if (data?.pdFile?.[0]) {
      const file = data.pdFile[0];
      const bodyData = {
        ...body,
        ProgramDescripitorFileName: file.name || "",
        ProgramDescripitorFileSize: file.size || "",
      };
      setBodyDataForUpdate(bodyData);
      uploadPDFile({ file });
    } else {
      updateProgramDescriptor({ body, programDescriptorId });
    }
  };

  const returnLabel = (labelArray, key) => {
    if (labelArray?.length > 0 && key) {
      return labelArray.find((x) => x.Key === key)?.Phrase || "";
    }
    return "";
  };

  const fetchMngResoRepoDtlsLabel = (key) => {
    if (resourcesCMSData?.pageContent?.["ManageResourceRepoDetails"]) {
      return returnLabel(
        resourcesCMSData.pageContent["ManageResourceRepoDetails"].Labels,
        key
      );
    }
    return "";
  };

  useEffect(() => {
    if (dataOnPDFileUpload?.data?.IsSuccess) {
      const bodyData = {
        ...bodyDataForUpdate,
        ProgramDescripitorURL: dataOnPDFileUpload.data.ProgramDescripitorURL,
        ProgramDescripitorFileGuid: dataOnPDFileUpload.data.Id,
      };
      updateProgramDescriptor({ body: bodyData, programDescriptorId });
    }
  }, [dataOnPDFileUpload?.data?.IsSuccess]);

  useEffect(() => {
    if (isSuccessOnUpdate) navigate(managePDViewPath);
  }, [isSuccessOnUpdate, navigate]);

  useEffect(() => {
    if (isSuccessOnDownload) {
      saveAs(
        dataOnDownload,
        `${programDescriptorById?.ProgramDescripitorFileName}`
      );
    }
  }, [isSuccessOnDownload, dataOnDownload, programDescriptorById]);

  const [disabledUpdateButton, setDisabledUpdateButton] = useState(false);

  useEffect(() => {
    if (!programDescriptorById?.IsActive) {
      setDisabledUpdateButton(true);
    } else if (hasA4Access) {
      setDisabledUpdateButton(true);
    } else {
      setDisabledUpdateButton(false);
    }
  }, [programDescriptorById, hasA4Access]);

  const arrayOfOnlyStandardPDTitles = [];
  filteredPDListForStandardType.forEach((eachItem) => {
    if (eachItem?.Id !== programDescriptorById?.Id) {
      arrayOfOnlyStandardPDTitles.push(eachItem?.ProgramDescripitorTitle);
    }
  });

  const [standardPDTitleFieldErrorMsg, setStandardPDTitleFieldErrorMsg] =
    useState("");

  useEffect(() => {
    if (arrayOfOnlyStandardPDTitles.includes(inputPDTitle?.trim())) {
      setStandardPDTitleFieldErrorMsg(
        "This standard resource title already exists in records."
      );
    } else {
      setStandardPDTitleFieldErrorMsg("");
    }
  }, [inputPDTitle, PDData, programDescriptorById]);

  const { trackActivity } = useTrackUserActivity();

  useEffect(() => {
    if (isSuccessOnDownload) {
      trackActivity({
        activitySuccess: !!isSuccessOnDownload,
        actionType: "Downloaded",
        itemId: fileDetails?.ProgramDescripitorFileGuid || "",
      });
    }
    if (isSuccessOnPDFileUpload) {
      const { fileGUID } = getGUIDAndExtentionFromFileURL({
        fileURL: dataOnPDFileUpload?.data?.FileUrl,
      });
      trackActivity({
        activitySuccess: !!isSuccessOnPDFileUpload,
        actionType: "Uploaded",
        itemId: fileGUID || "",
      });
    }
  }, [isSuccessOnDownload, isSuccessOnPDFileUpload]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {fetchMngResoRepoDtlsLabel(
          "ManageResourceRepoDetails.UpdateStandardResource"
        )}
      </Typography>

      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button
          variant="outlined"
          size="small"
          component={Link}
          to={managePDViewPath}
        >
          {fetchMngResoRepoDtlsLabel("ManageResourceRepoDetails.Resources")}
        </Button>
      </Stack>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={rhfFormInstance.handleSubmit(onSubmit)}
      >
        <MpgItem>
          <MpgInput
            rhfFormInstance={rhfFormInstance}
            name="pdTitle"
            label={fetchMngResoRepoDtlsLabel(
              "ManageResourceRepoDetails.StandardResourceTitle"
            )}
            fullWidth
          />

          <FormHelperText error={!!standardPDTitleFieldErrorMsg}>
            {standardPDTitleFieldErrorMsg}
          </FormHelperText>

          {canViewNewFields && (
            <>
              {/* Row 1 — Countries + Language */}
              <Grid
                container
                spacing={2}
                sx={{ mt: 1 }}
                alignItems="flex-start"
              >
                {/* Countries */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="countries"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={countryNames}
                          value={field.value || []}
                          onChange={(_, newValue) => {
                            field.onChange(newValue ?? []);
                            rhfFormInstance.trigger("countries");
                          }}
                          disabled={isMasterDataLoading}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              minHeight: "40px",
                              maxHeight: "40px",
                              overflow: "hidden",
                              flexWrap: "nowrap",
                              padding: "0px 8px !important",
                            },
                            "& .MuiAutocomplete-tag": {
                              maxWidth: "calc(50% - 8px)",
                            },
                          }}
                          renderOption={(props, option, { selected }) => (
                            <MenuItem {...props} dense>
                              <Checkbox
                                checked={selected}
                                size="small"
                                sx={{ py: 0.5 }}
                              />
                              <ListItemText primary={option} />
                            </MenuItem>
                          )}
                          renderTags={(selected, getTagProps) =>
                            selected.map((val, index) => (
                              <Chip
                                key={val}
                                label={val}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label={
                                isMasterDataLoading
                                  ? fetchMngResoRepoDtlsLabel(
                                      "ManageResourceRepoDetails.LoadingCountries"
                                    ) || "Loading Countries..."
                                  : fetchMngResoRepoDtlsLabel(
                                      "ManageResourceRepoDetails.Countries"
                                    ) + " *"
                              }
                              placeholder={
                                field.value?.length === 0 ? "Countries" : ""
                              }
                              error={!!errors.countries}
                              helperText={errors.countries?.message}
                              InputLabelProps={{ shrink: true }}
                              inputProps={{
                                ...params.inputProps,
                                style: { fontSize: "13px", padding: "0px" },
                              }}
                            />
                          )}
                          ListboxProps={{ sx: { maxHeight: 280 } }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Language */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={languageRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="language"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.language}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Language"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value ?? ""}
                            displayEmpty
                            disabled={isMasterDataLoading}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("language");
                            }}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Language"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(languageRef)}
                            renderValue={(val) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingLanguages"
                                  )}
                                </Typography>
                              ) : val ? (
                                <Typography fontSize={13}>{val}</Typography>
                              ) : (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Language"
                                  )}
                                </Typography>
                              )
                            }
                          >
                            {languageOptions.map((lang) => (
                              <MenuItem
                                key={lang.LocaleKey}
                                value={lang.LocaleName}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                {lang.LocaleName}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.language && (
                            <FormHelperText>
                              {errors.language.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Row 2 — Owner + Duration */}
              <Grid
                container
                spacing={2}
                sx={{ mt: 1 }}
                alignItems="flex-start"
              >
                {/* Owner */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={ownerRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="owner"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.owner}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Owner"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            {...field}
                            value={field.value ?? ""}
                            displayEmpty
                            disabled={isMasterDataLoading}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("owner");
                            }}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Owner"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(ownerRef)}
                            renderValue={(val) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingOwners"
                                  )}
                                </Typography>
                              ) : val ? (
                                <Typography fontSize={13}>{val}</Typography>
                              ) : (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Owner"
                                  )}
                                </Typography>
                              )
                            }
                          >
                            {owners.map((owner) => (
                              <MenuItem
                                key={owner}
                                value={owner}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                {owner}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.owner && (
                            <FormHelperText>
                              {errors.owner.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>

                {/* Duration */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ position: "relative" }} ref={durationRef}>
                    {isMasterDataLoading && <SelectSpinner />}
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.duration}
                        >
                          <InputLabel shrink>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.Duration"
                            )}{" "}
                            *
                          </InputLabel>
                          <Select
                            {...field}
                            multiple
                            value={field.value ?? []}
                            disabled={isMasterDataLoading}
                            onChange={(e) => {
                              field.onChange(e);
                              rhfFormInstance.trigger("duration");
                            }}
                            input={
                              <OutlinedInput
                                notched
                                label={`${fetchMngResoRepoDtlsLabel(
                                  "ManageResourceRepoDetails.Duration"
                                )} *`}
                              />
                            }
                            sx={fixedSelectSx}
                            MenuProps={bottomMenuProps(durationRef)}
                            renderValue={(selected) =>
                              isMasterDataLoading ? (
                                <Typography fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.LoadingDurations"
                                  )}
                                </Typography>
                              ) : selected.length === 0 ? (
                                <Typography color="text.disabled" fontSize={13}>
                                  {fetchMngResoRepoDtlsLabel(
                                    "ManageResourceRepoDetails.Duration"
                                  )}
                                </Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    gap: 0.5,
                                    overflow: "hidden",
                                  }}
                                >
                                  {selected.map((val) => (
                                    <Chip
                                      key={val}
                                      label={val}
                                      size="small"
                                      onDelete={() =>
                                        field.onChange(
                                          (field.value ?? []).filter(
                                            (v) => v !== val
                                          )
                                        )
                                      }
                                      onMouseDown={(e) => e.stopPropagation()}
                                    />
                                  ))}
                                </Box>
                              )
                            }
                          >
                            {durationOptions.map((duration) => (
                              <MenuItem
                                key={duration}
                                value={duration}
                                dense
                                sx={{ fontSize: 13, py: 0.75 }}
                              >
                                <Checkbox
                                  checked={(field.value ?? []).includes(
                                    duration
                                  )}
                                  size="small"
                                />
                                <ListItemText
                                  primary={duration}
                                  primaryTypographyProps={{ fontSize: 13 }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.duration && (
                            <FormHelperText>
                              {errors.duration.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Row 3 — Resource Updated (only shown when file is modified) */}
              {isFileModified && (
                <Grid
                  container
                  spacing={2}
                  sx={{ mt: 1 }}
                  alignItems="flex-start"
                >
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="resourceUpdated"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.resourceUpdated}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "4px",
                              backgroundColor: "#fff",
                              minHeight: 40,
                            },
                            "& .MuiInputLabel-root": { fontSize: "0.95rem" },
                            "& .MuiFormHelperText-root": {
                              marginLeft: "4px",
                              marginTop: "4px",
                            },
                          }}
                        >
                          <InputLabel shrink required>
                            {fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.ResourceUpdated"
                            )}
                          </InputLabel>
                          <OutlinedInput
                            type="text"
                            notched
                            disabled
                            label={`${fetchMngResoRepoDtlsLabel(
                              "ManageResourceRepoDetails.ResourceUpdated"
                            )}`}
                            value={new Date().toLocaleDateString("en-GB")}
                            inputProps={{ readOnly: true }}
                            sx={{
                              "& .MuiOutlinedInput-input": {
                                cursor: "not-allowed",
                                WebkitTextFillColor: "rgba(0,0,0,0.6)",
                              },
                            }}
                          />
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {/* File Upload */}
          <Box sx={{ mt: 2 }}>
            <MpgFileUpload
              name="pdFile"
              label={fetchMngResoRepoDtlsLabel(
                "ManageResourceRepoDetails.UploadFile"
              )}
              rhfFormInstance={rhfFormInstance}
              persistantMsg={fetchMngResoRepoDtlsLabel(
                "ManageResourceRepoDetails.AllowedFile"
              )}
            />
            {!!programDescriptorById?.ProgramDescripitorFileName && (
              <MuiLink
                sx={{ cursor: "pointer", display: "inline-block", mt: 1 }}
                onClick={() => {
                  const fileName = fileDetails?.ProgramDescripitorFileName;
                  const fileExtension = fileName.split(".").at(-1);
                  if (fileDetails?.ProgramDescripitorFileGuid) {
                    DownloadPDfile({
                      fileGUID: fileDetails?.ProgramDescripitorFileGuid,
                      DownloadType: "programDescriptor",
                      Extension: fileExtension,
                    });
                  }
                }}
              >
                {programDescriptorById?.ProgramDescripitorFileName}
              </MuiLink>
            )}
          </Box>
        </MpgItem>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-evenly"
          mt={3}
        >
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={
              !!disabledUpdateButton ||
              !!standardPDTitleFieldErrorMsg ||
              isMasterDataLoading
            }
            startIcon={
              isMasterDataLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {isMasterDataLoading
              ? "Loading..."
              : fetchMngResoRepoDtlsLabel(
                  "ManageResourceRepoDetails.UpdateProgrammeDescriptor"
                )}
          </Button>
        </Stack>
      </Box>

      <MpgErrorDialog
        open={
          !!isErrorOnUpdate || !!isErrorOnPDFileUpload || !!isErrorOnDownload
        }
        title={
          errorOnPDFileUpload?.response?.data?.ErrorCode === "104" &&
          fetchMngResoRepoDtlsLabel(
            "ManageResourceRepoDetails.ErrorMsgFileScanFailed"
          )
        }
      />
      <MpgLoadingDialog
        open={!!isLoadingOnUpdate || !!isLoadingOnPDFileUpload}
      />
    </Box>
  );
};

export default EditProgrammeDescriptor;
